if (!customElements.get('quick-add-modal')) {
  customElements.define('quick-add-modal', class QuickAddModal extends ModalDialog {
    constructor() {
      super();
      this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
    }

    hide(preventFocus = false) {
      // Manual dismiss may animate; post-add hide(true) is always immediate.
      if (preventFocus) window.pnMotion?.cancelQuickClose?.(this);
      if (!preventFocus && this.hasAttribute('open') &&
          window.pnMotion?.deferQuickClose?.(this, () => this.hide(false))) return;
      const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      if (cartNotification) cartNotification.setActiveElement(this.openedBy);
      this.loadController?.abort();
      this.modalContent.innerHTML = '';

      if (preventFocus) this.openedBy = null;
      super.hide();
    }

    async show(opener) {
      window.pnMotion?.cancelQuickClose?.(this);
      if (opener.getAttribute('aria-disabled') === 'true') return;
      const card = opener.closest('[data-pn-card], .card-wrapper') || opener.parentElement;
      card.querySelector('.pn-quick-error')?.remove();
      this.loadController?.abort();
      const controller = new AbortController();
      this.loadController = controller;
      opener.setAttribute('aria-disabled', 'true');
      opener.classList.add('loading');
      const spinner = opener.querySelector('.loading-overlay__spinner');
      spinner?.classList.remove('hidden');
      const requestedUrl = opener.dataset.productUrl;
      const timeout = setTimeout(() => controller.abort(), 20000);
      try {
        const response = await fetch(requestedUrl, { credentials: 'same-origin', signal: controller.signal });
        if (!response.ok) throw new Error(`Product HTTP ${response.status}`);
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        this.productElement = html.querySelector('section[id^="MainProduct-"]');
        if (!this.productElement?.querySelector('product-form')) throw new Error('Product form unavailable');
        // Product CSS sits outside MainProduct and was lost in the old fragment import.
        const assetLinks = [...html.querySelectorAll('link[rel="stylesheet"]')].filter(link => /\/(?:pn-product|component-product|section-main-product)[^/]*\.css/.test(link.href));
        assetLinks.forEach(link => {
          if (![...document.querySelectorAll('link[rel="stylesheet"]')].some(existing => existing.href === link.href)) document.head.append(link.cloneNode());
        });
        // Collections do not load the PDP enhancement that keeps option labels in sync.
        const enhancement = [...html.querySelectorAll('script[src]')].find(script => /\/pn-product\.js(?:\?|$)/.test(script.src));
        if (enhancement) await this.loadDependency(enhancement.src);
        // Wait for the actual product dependencies before inserting and upgrading the fragment.
        for (const script of this.productElement.querySelectorAll('script[src]')) {
          if (!/\/(?:media-gallery|product-modal|product-model|pickup-availability|magnify)\.js/.test(script.src)) continue;
          await this.loadDependency(script.src);
          script.remove();
        }
        if (controller.signal.aborted || opener.dataset.productUrl !== requestedUrl) throw new Error('Product load superseded');
        document.querySelectorAll('quick-add-modal[open]').forEach(modal => { if (modal !== this) modal.hide(); });
        this.preventDuplicatedIDs();
        this.removeDOMElements();
        const gallery = this.productElement.querySelector('media-gallery');
        if (gallery) gallery.dataset.disablePrepend = 'false';
        this.modalContent.classList.add('pn-product');
        this.prepareQuickLayout();
        this.updateShippingContext();
        this.setInnerHTML(this.modalContent, this.productElement.outerHTML);
        this.modalContent.querySelector('.pn-quick-scroll')?.scrollTo(0, 0);
        // Card selection takes priority in Quick add even when the PDP intentionally
        // starts with a fixed hero image (display_variant_image_first=false).
        const loadedGallery = this.modalContent.querySelector('media-gallery');
        const variantMediaId = opener.dataset.variantMediaId;
        if (loadedGallery && variantMediaId) {
          const selectedMedia = [...loadedGallery.querySelectorAll('[data-media-id]')]
            .find(media => media.dataset.mediaId.endsWith('-' + variantMediaId));
          if (selectedMedia) loadedGallery.setActiveMedia?.(selectedMedia.dataset.mediaId, true);
        }
        this.removeGalleryListSemantic();
        this.updateImageSizes();
        this.preventVariantURLSwitching();
        window.Shopify?.PaymentButton?.init();
        window.ProductModel?.loadShopifyXR();
        this.dispatchEvent(new CustomEvent('pn:quick-add:loaded', { bubbles: true }));
        super.show(opener);
        const closeButton = this.querySelector('[id^="ModalClose-"]');
        if (closeButton) {
          trapFocus(this, closeButton);
          closeButton.focus({ preventScroll: true });
        }
      } catch (error) {
        if (opener.dataset.productUrl !== requestedUrl) return;
        this.modalContent.replaceChildren();
        const alert = document.createElement('div');
        alert.className = 'pn-quick-error';
        alert.setAttribute('role', 'alert');
        alert.append(document.createTextNode((this.dataset.error || 'Unable to load options. Please try again.') + ' '));
        const link = document.createElement('a');
        link.href = opener.dataset.productUrl;
        link.textContent = this.dataset.viewProduct || 'View product';
        alert.append(link);
        (card.querySelector('.quick-add') || card).append(alert);
      } finally {
        clearTimeout(timeout);
        opener.removeAttribute('aria-disabled');
        opener.classList.remove('loading');
        spinner?.classList.add('hidden');
        if (this.loadController === controller) {
          this.loadController = null;
          if (opener.isConnected && opener.dataset.productUrl !== requestedUrl) this.show(opener);
        }
      }
    }

    prepareQuickLayout() {
      // Keep the PDP scope and native product-info/form IDs together. Only the
      // modal gets this compact overview, scroll body and persistent native CTA.
      const section = this.productElement;
      const sectionId = section.dataset.section;
      section.id = `MainProduct-quickadd-${this.id}-${sectionId}`;
      section.classList.add('pn-quick-product');
      const info = section.querySelector('product-info');
      if (!info) return;
      const scroll = document.createElement('div');
      scroll.className = 'pn-quick-scroll';
      const buyButtons = [...info.children].find(node => node.dataset.pnProductBlock === 'buy_buttons');
      [...info.childNodes].forEach(node => { if (node !== buyButtons) scroll.append(node); });
      info.prepend(scroll);
      if (buyButtons) buyButtons.classList.add('pn-quick-actions');
      const overview = document.createElement('div');
      overview.className = 'pn-quick-overview';
      const heading = document.createElement('div');
      heading.className = 'pn-quick-heading';
      // Move existing nodes; the variant engine continues to address their IDs.
      [...scroll.children].filter(node => ['title', 'price', 'an_rating'].includes(node.dataset.pnProductBlock))
        .forEach(node => heading.append(node));
      const media = section.querySelector('.product__media-wrapper');
      if (media) overview.append(media);
      overview.append(heading);
      scroll.prepend(overview);
      if (section.hasAttribute('data-nibosi-bogo-page') && buyButtons) {
        // Keep the original offer/form before its benefits in one scroll flow.
        (scroll.querySelector('variant-selects') || overview).after(buyButtons);
        section.dataset.nibosiQuickOffer = '';
      }
      section.querySelectorAll('details.product-info__accordion').forEach(details => {
        details.removeAttribute('aria-expanded');
        details.removeAttribute('is');
      });
    }

    updateShippingContext() {
      // Shopify's rendered localization is authoritative; never infer country
      // from language, currency, an IP service or a hardcoded UK default.
      const code = (this.productElement.dataset.pnCountryCode || window.Shopify?.country || '').toUpperCase();
      let name = this.productElement.dataset.pnCountryName || '';
      if (!name && /^[A-Z]{2}$/.test(code)) {
        try { name = new Intl.DisplayNames(['en'], { type: 'region' }).of(code); } catch (_) { /* neutral fallback */ }
      }
      const excluded = ['TW', 'BR', 'DO', 'CY', 'NI', 'IN', 'PK', 'GL'].includes(code);
      this.productElement.querySelectorAll('.shipping-info').forEach(info => {
        const title = info.querySelector('.shipping-title');
        const delivery = info.querySelector('.shipping-delivery');
        if (!title || !delivery) return;
        title.textContent = excluded ? `Shipping unavailable to ${name || code}`
          : name ? `FREE SHIPPING to ${name}` : 'FREE SHIPPING to supported destinations';
        delivery.replaceChildren();
        const timing = document.createElement('span');
        timing.className = 'shipping-days';
        timing.textContent = excluded ? 'We do not currently ship to this destination.'
          : 'Processing: 3 business days. Estimated delivery: 10–20 business days after processing.';
        delivery.append(timing);
        const note = document.createElement('span');
        note.className = 'pn-quick-shipping-note';
        note.textContent = excluded ? 'Choose a supported shipping destination to order.'
          : 'Free shipping also applies during promotions.';
        info.append(note);
        info.dataset.pnShippingCountry = code;
      });
    }

    loadDependency(src) {
      const name = new URL(src).pathname.split('/').pop().replace('.js', '');
      if (customElements.get(name)) return Promise.resolve();
      const cache = window.pnQuickAddDependencies ||= new Map();
      if (!cache.has(src)) cache.set(src, new Promise((resolve, reject) => {
        const script = document.createElement('script');
        const timer = setTimeout(() => { script.remove(); cache.delete(src); reject(new Error('Product dependency timed out')); }, 15000);
        script.src = src;
        script.onload = () => { clearTimeout(timer); resolve(); };
        script.onerror = () => { clearTimeout(timer); script.remove(); cache.delete(src); reject(new Error('Product dependency unavailable')); };
        document.head.append(script);
      }));
      return cache.get(src);
    }

    setInnerHTML(element, html) {
      element.innerHTML = html;

      // Reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
      element.querySelectorAll('script').forEach(oldScriptTag => {
        const newScriptTag = document.createElement('script');
        Array.from(oldScriptTag.attributes).forEach(attribute => {
          newScriptTag.setAttribute(attribute.name, attribute.value)
        });
        newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
        oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
      });
    }

    preventVariantURLSwitching() {
      const variantPicker = this.modalContent.querySelector('variant-radios,variant-selects');
      if (!variantPicker) return;

      variantPicker.setAttribute('data-update-url', 'false');
    }

    removeDOMElements() {
      const pickupAvailability = this.productElement.querySelector('pickup-availability');
      if (pickupAvailability) pickupAvailability.remove();

      const productModal = this.productElement.querySelector('product-modal');
      if (productModal) productModal.remove();

      const stickyAtc = this.productElement.querySelector('sticky-atc');
      if (stickyAtc) stickyAtc.remove();

      const modalDialog = this.productElement.querySelectorAll('modal-dialog');
      if (modalDialog) modalDialog.forEach(modal => modal.remove());
    }

    preventDuplicatedIDs() {
      const sectionId = this.productElement.dataset.section;
      this.productElement.innerHTML = this.productElement.innerHTML.replaceAll(sectionId, `quickadd-${this.id}-${ sectionId }`);
      this.productElement.querySelectorAll('variant-selects, variant-radios, product-info').forEach((element) => {
        element.dataset.originalSection = sectionId;
      });
    }

    removeGalleryListSemantic() {
      const galleryList = this.modalContent.querySelector('[id^="Slider-Gallery"]');
      if (!galleryList) return;

      galleryList.setAttribute('role', 'presentation');
      galleryList.querySelectorAll('[id^="Slide-"]').forEach(li => li.setAttribute('role', 'presentation'));
    }

    updateImageSizes() {
      this.modalContent.querySelectorAll('.product__media img').forEach(img => {
        img.setAttribute('sizes', '(min-width: 600px) 112px, 84px');
      });
    }
  });
}
